import { Option } from '../../../shared/models/option.model';

export interface Model184Validation {
  key: string | null;
  type: string;
  message: string;
}

export interface Model184Renta {
  uid: string | null;
  origen: string;
  clave: string;
  subclave: string;
  pais: string;
  regimen: string;
  tipoActividad: string;
  iae: string;
  nifCesionario: string;
  denominacionCesionario: string;
  fechaAdquisicion: string;
  valorAumentos: number;
  valorDisminuciones: number;
  ingresos: number;
  gastos: number;
  renta: number;
  porcentajeReduccion: number;
  rentaReduccion: number;
  gananciasPerdidas: number;
  baseDeduccion: number;
  retenciones: number;
  situacionInmueble: string;
  referenciaCatastral: string;
  criterioCobros: boolean;
  diasArrendamiento: number;
  gastosActividad: { [concepto: string]: number };
  gastosInmueble: { [concepto: string]: number };
  documentos: number;
}

export interface Model184Miembro {
  uid: string | null;
  origen: string;
  nif: string;
  nifRepresentante: string;
  nombre: string;
  provincia: string;
  pais: string;
  tipoParticipe: string;
  miembroA31: boolean;
  dias: number;
  participacion: number;
  domicilio: string;
  naturalezaInmueble: string;
  situacionInmueble: string;
  referenciaCatastral: string;
  claveDeclarado: string;
  porcentajeTitularidad: number | null;
  diasArrendamiento: number;
}

export interface Model184Atribucion {
  nifMiembro: string;
  nombreMiembro: string;
  claveEntidad: string;
  subclaveEntidad: string;
  clave: string;
  subclave: string;
  importe: number;
  reduccion: number;
  provisiones: number;
}

export interface Model184 {
  modelo: string;
  periodo?: string;
  ejercicio: number;
  sujetoNIF: string;
  sujetoDenominacion: string;
  telefono: string;
  contacto: string;
  complementaria: boolean;
  sustitutiva: boolean;
  justificanteAnterior: string;
  justificante: string;
  tipoEntidad: string;
  actividadPrincipal: string;
  extranjeroTipo: string;
  extranjeroObjeto: string;
  extranjeroPais: string;
  porcentajeResidentes: number;
  tributaIS: boolean;
  cifraNegocios: number;
  representanteNIF: string;
  representanteNombre: string;
  rentas: Model184Renta[];
  miembros: Model184Miembro[];
  atribuciones: Model184Atribucion[];
  numeroMiembros: number;
  numeroRegistrosEntidad: number;
  avisos: string[];
}

export interface Model184FieldOriginQuery {
  campo: string;
  taxYear: number;
  clave: string;
  subclave: string;
  valorEnElFormulario: number;
}

export const GASTOS_ACTIVIDAD_184: { clave: string, etiqueta: string }[] = [
  { clave: 'personal', etiqueta: 'Gastos de personal' },
  { clave: 'consumos', etiqueta: 'Consumos de explotación' },
  { clave: 'tributos', etiqueta: 'Tributos fiscalmente deducibles' },
  { clave: 'arrendamientos', etiqueta: 'Arrendamientos y cánones' },
  { clave: 'reparaciones', etiqueta: 'Reparaciones y conservación' },
  { clave: 'servicios', etiqueta: 'Servicios de profesionales independientes' },
  { clave: 'suministros', etiqueta: 'Suministros' },
  { clave: 'financieros', etiqueta: 'Gastos financieros' },
  { clave: 'amortizaciones', etiqueta: 'Amortizaciones' },
  { clave: 'provisiones', etiqueta: 'Provisiones' },
  { clave: 'otros', etiqueta: 'Otros gastos' }
];

export const GASTOS_INMUEBLE_184: { clave: string, etiqueta: string }[] = [
  { clave: 'intereses', etiqueta: 'Intereses y gastos de financiación' },
  { clave: 'conservacion', etiqueta: 'Reparación y conservación' },
  { clave: 'pendientes', etiqueta: 'Gastos pendientes de ejercicios anteriores' },
  { clave: 'tributos', etiqueta: 'Tributos y recargos' },
  { clave: 'dudosoCobro', etiqueta: 'Saldos de dudoso cobro' },
  { clave: 'terceros', etiqueta: 'Servicios de terceros' },
  { clave: 'seguros', etiqueta: 'Primas de seguros' },
  { clave: 'amortizacionInmueble', etiqueta: 'Amortización del inmueble' },
  { clave: 'amortizacionMuebles', etiqueta: 'Amortización de muebles' },
  { clave: 'otros', etiqueta: 'Otros gastos' }
];

function detalle(origen: { [concepto: string]: number }, conceptos: { clave: string }[]): { [concepto: string]: number } {
  const resultado: { [concepto: string]: number } = {};

  conceptos.forEach(item => {
    resultado[item.clave] = Number((origen || {})[item.clave]) || 0;
  });

  return resultado;
}

export function nuevaRenta184(): Model184Renta {
  return normalizarRenta184({} as Model184Renta);
}

export function normalizarRenta184(origen: Model184Renta): Model184Renta {
  const r: any = origen || {};

  return {
    uid: r.uid || null,
    origen: r.origen || 'MANUAL',
    clave: r.clave || 'D',
    subclave: r.subclave ?? '01',
    pais: r.pais || '',
    regimen: r.regimen || '',
    tipoActividad: r.tipoActividad || '',
    iae: r.iae || '',
    nifCesionario: r.nifCesionario || '',
    denominacionCesionario: r.denominacionCesionario || '',
    fechaAdquisicion: r.fechaAdquisicion || '',
    valorAumentos: Number(r.valorAumentos) || 0,
    valorDisminuciones: Number(r.valorDisminuciones) || 0,
    ingresos: Number(r.ingresos) || 0,
    gastos: Number(r.gastos) || 0,
    renta: Number(r.renta) || 0,
    porcentajeReduccion: Number(r.porcentajeReduccion) || 0,
    rentaReduccion: Number(r.rentaReduccion) || 0,
    gananciasPerdidas: Number(r.gananciasPerdidas) || 0,
    baseDeduccion: Number(r.baseDeduccion) || 0,
    retenciones: Number(r.retenciones) || 0,
    situacionInmueble: r.situacionInmueble || '',
    referenciaCatastral: r.referenciaCatastral || '',
    criterioCobros: !!r.criterioCobros,
    diasArrendamiento: Number(r.diasArrendamiento) || 0,
    gastosActividad: detalle(r.gastosActividad, GASTOS_ACTIVIDAD_184),
    gastosInmueble: detalle(r.gastosInmueble, GASTOS_INMUEBLE_184),
    documentos: Number(r.documentos) || 0
  };
}

export function nuevoMiembro184(): Model184Miembro {
  return normalizarMiembro184({} as Model184Miembro);
}

export function normalizarMiembro184(origen: Model184Miembro): Model184Miembro {
  const m: any = origen || {};

  return {
    uid: m.uid || null,
    origen: m.origen || 'MANUAL',
    nif: m.nif || '',
    nifRepresentante: m.nifRepresentante || '',
    nombre: m.nombre || '',
    provincia: m.provincia || '',
    pais: m.pais || '',
    tipoParticipe: m.tipoParticipe || '1',
    miembroA31: m.miembroA31 === undefined ? true : !!m.miembroA31,
    dias: m.dias === undefined || m.dias === null ? 365 : Number(m.dias),
    participacion: Number(m.participacion) || 0,
    domicilio: m.domicilio || '',
    naturalezaInmueble: m.naturalezaInmueble || '',
    situacionInmueble: m.situacionInmueble || '',
    referenciaCatastral: m.referenciaCatastral || '',
    claveDeclarado: m.claveDeclarado || '',
    porcentajeTitularidad: m.porcentajeTitularidad ?? null,
    diasArrendamiento: Number(m.diasArrendamiento) || 0
  };
}

export function nuevoModelo184(ejercicio: number): Model184 {
  return normalizarModelo184({ ejercicio } as Model184);
}

export function normalizarModelo184(origen: Model184): Model184 {
  const m: any = origen || {};

  return {
    modelo: '184',
    periodo: m.periodo,
    ejercicio: Number(m.ejercicio) || 0,
    sujetoNIF: m.sujetoNIF || '',
    sujetoDenominacion: m.sujetoDenominacion || '',
    telefono: m.telefono || '',
    contacto: m.contacto || '',
    complementaria: !!m.complementaria,
    sustitutiva: !!m.sustitutiva,
    justificanteAnterior: m.justificanteAnterior || '',
    justificante: m.justificante || '',
    tipoEntidad: m.tipoEntidad || '',
    actividadPrincipal: m.actividadPrincipal || '',
    extranjeroTipo: m.extranjeroTipo || '',
    extranjeroObjeto: m.extranjeroObjeto || '',
    extranjeroPais: m.extranjeroPais || '',
    porcentajeResidentes: m.porcentajeResidentes === undefined || m.porcentajeResidentes === null
      ? 100 : Number(m.porcentajeResidentes),
    tributaIS: !!m.tributaIS,
    cifraNegocios: Number(m.cifraNegocios) || 0,
    representanteNIF: m.representanteNIF || '',
    representanteNombre: m.representanteNombre || '',
    rentas: (m.rentas || []).map((item: Model184Renta) => normalizarRenta184(item)),
    miembros: (m.miembros || []).map((item: Model184Miembro) => normalizarMiembro184(item)),
    atribuciones: m.atribuciones || [],
    numeroMiembros: Number(m.numeroMiembros) || 0,
    numeroRegistrosEntidad: Number(m.numeroRegistrosEntidad) || 0,
    avisos: m.avisos || []
  };
}

export const TIPO_DECLARACION_184: Option[] = [
  { label: 'Normal', value: 'N' },
  { label: 'Complementaria', value: 'C' },
  { label: 'Sustitutiva', value: 'S' }
];

export const TIPO_ENTIDAD_184: Option[] = [
  { label: '1 - Sociedad civil', value: '1' },
  { label: '2 - Comunidad de bienes', value: '2' },
  { label: '3 - Herencia yacente', value: '3' },
  { label: '4 - Comunidad de propietarios', value: '4' },
  { label: '5 - Otros', value: '5' }
];

export const ACTIVIDAD_PRINCIPAL_184: Option[] = [
  { label: '1 - Actividad empresarial', value: '1' },
  { label: '2 - Actividad profesional', value: '2' },
  { label: '3 - Tenencia y administración de bienes inmuebles', value: '3' },
  { label: '4 - Tenencia y administración de valores o activos financieros', value: '4' },
  { label: '5 - Otras', value: '5' }
];

export const EXTRANJERO_TIPO_184: Option[] = [
  { label: 'No, constituida en España', value: '' },
  { label: '1 - Corporación, asociación o ente con personalidad jurídica propia', value: '1' },
  { label: '2 - Corporación o ente independiente sin personalidad jurídica propia', value: '2' },
  { label: '3 - Conjunto unitario de bienes de dos o más personas sin personalidad jurídica', value: '3' },
  { label: '4 - Otras', value: '4' }
];

export const EXTRANJERO_OBJETO_184: Option[] = [
  { label: 'A - Actividad de naturaleza empresarial', value: 'A' },
  { label: 'B - Actividad de naturaleza profesional', value: 'B' }
];

export const CLAVE_RENTA_184: Option[] = [
  { label: 'A - Rendimientos del capital mobiliario', value: 'A' },
  { label: 'B - Persona o entidad cesionaria de capitales propios', value: 'B' },
  { label: 'C - Rendimientos del capital inmobiliario', value: 'C' },
  { label: 'D - Rendimientos de actividades económicas', value: 'D' },
  { label: 'E - Rentas de instituciones de inversión colectiva', value: 'E' },
  { label: 'F - Ganancias y pérdidas patrimoniales no derivadas de transmisiones', value: 'F' },
  { label: 'G - Ganancias y pérdidas patrimoniales derivadas de transmisiones', value: 'G' },
  { label: 'H - Entidades que determinan la renta según el Impuesto sobre Sociedades', value: 'H' },
  { label: 'I - Deducciones de la Ley del IRPF', value: 'I' },
  { label: 'J - Deducciones de la Ley del Impuesto sobre Sociedades', value: 'J' },
  { label: 'K - Retenciones e ingresos a cuenta soportados', value: 'K' },
  { label: 'L - Exceso de rentas negativas en países sin convenio', value: 'L' }
];

export const SUBCLAVES_184: { [clave: string]: Option[] } = {
  A: [
    { label: '01 - Rendimientos obtenidos en España', value: '01' },
    { label: '02 - Rendimientos obtenidos en el extranjero', value: '02' },
    { label: '03 - Reducciones aplicables', value: '03' }
  ],
  C: [
    { label: '01 - Rendimientos obtenidos en España', value: '01' },
    { label: '02 - Rendimientos obtenidos en el extranjero', value: '02' },
    { label: '03 - Reducciones aplicables', value: '03' }
  ],
  D: [
    { label: '01 - Rendimientos obtenidos en España', value: '01' },
    { label: '02 - Rendimientos obtenidos en el extranjero', value: '02' },
    { label: '03 - Rendimiento con derecho a reducción', value: '03' }
  ],
  F: [
    { label: '01 - Ganancias generadas en España', value: '01' },
    { label: '02 - Pérdidas generadas en España', value: '02' },
    { label: '03 - Ganancias generadas en el extranjero', value: '03' },
    { label: '04 - Pérdidas generadas en el extranjero', value: '04' }
  ],
  G: [
    { label: '01 - Ganancias sin reducción generadas en España', value: '01' },
    { label: '02 - Pérdidas sin reducción generadas en España', value: '02' },
    { label: '03 - Ganancias sin reducción generadas en el extranjero', value: '03' },
    { label: '04 - Pérdidas sin reducción generadas en el extranjero', value: '04' },
    { label: '05 - Ganancias con reducción, elementos no afectos, en España', value: '05' },
    { label: '06 - Ganancias con reducción, elementos no afectos, en el extranjero', value: '06' },
    { label: '07 - Ganancias con reducción, elementos afectos, en España', value: '07' },
    { label: '08 - Ganancias con reducción, elementos afectos, en el extranjero', value: '08' }
  ],
  I: [
    { label: '01 - Protección del Patrimonio Español y Mundial', value: '01' },
    { label: '02 - Donativos, donaciones y aportaciones a determinadas entidades', value: '02' },
    { label: '03 - Rentas obtenidas en Ceuta y Melilla', value: '03' },
    { label: '04 - Deducciones en actividades económicas', value: '04' },
    { label: '05 - Doble imposición internacional', value: '05' },
    { label: '06 - Inversión en empresas de nueva o reciente creación', value: '06' }
  ],
  J: [
    { label: '01 - Doble imposición internacional', value: '01' },
    { label: '02 - Deducciones con límite de cuota', value: '02' },
    { label: '03 - Donativos a entidades sin fines lucrativos', value: '03' },
    { label: '04 - Otras deducciones', value: '04' }
  ],
  K: [
    { label: '01 - Rendimientos del capital mobiliario', value: '01' },
    { label: '02 - Arrendamiento de inmuebles urbanos', value: '02' },
    { label: '03 - Actividades económicas', value: '03' },
    { label: '04 - Ganancias patrimoniales', value: '04' },
    { label: '05 - Otros conceptos', value: '05' }
  ],
  L: [
    { label: 'A - Rendimientos del capital mobiliario', value: 'A' },
    { label: 'C - Rendimientos del capital inmobiliario', value: 'C' },
    { label: 'D - Rendimientos de actividades económicas', value: 'D' },
    { label: 'E - Rentas de instituciones de inversión colectiva', value: 'E' },
    { label: 'F - Ganancias y pérdidas no derivadas de transmisiones', value: 'F' },
    { label: 'G - Ganancias y pérdidas derivadas de transmisiones', value: 'G' }
  ]
};

export const REGIMEN_184: Option[] = [
  { label: '1 - Estimación directa modalidad normal', value: '1' },
  { label: '2 - Estimación directa modalidad simplificada', value: '2' },
  { label: '3 - Estimación objetiva', value: '3' }
];

export const TIPO_ACTIVIDAD_184: Option[] = [
  { label: '1 - Actividades empresariales de carácter mercantil', value: '1' },
  { label: '2 - Actividades agrícolas y ganaderas', value: '2' },
  { label: '3 - Otras actividades empresariales de carácter no mercantil', value: '3' },
  { label: '4 - Actividades profesionales de carácter artístico o deportivo', value: '4' },
  { label: '5 - Restantes actividades profesionales', value: '5' }
];

export const SITUACION_INMUEBLE_184: Option[] = [
  { label: '1 - Con referencia catastral, resto del territorio español', value: '1' },
  { label: '2 - En el País Vasco', value: '2' },
  { label: '3 - En la Comunidad Foral de Navarra', value: '3' },
  { label: '4 - Sin referencia catastral', value: '4' },
  { label: '5 - En el extranjero', value: '5' }
];

export const NATURALEZA_INMUEBLE_184: Option[] = [
  { label: '1 - Inmueble urbano', value: '1' },
  { label: '2 - Inmueble rústico', value: '2' }
];

export const TIPO_PARTICIPE_184: Option[] = [
  { label: '1 - Residente', value: '1' },
  { label: '2 - No residente sin establecimiento permanente', value: '2' },
  { label: '3 - No residente con establecimiento permanente', value: '3' }
];

export const CLAVE_DECLARADO_184: Option[] = [
  { label: 'N - Nudo propietario', value: 'N' },
  { label: 'T - Titular', value: 'T' },
  { label: 'U - Usufructuario', value: 'U' },
  { label: 'O - Otro derecho real', value: 'O' }
];
